import { DinamikBilesen } from '@service/fikoyt-ui/decorators/dinamik-bilesen';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GameCardBase, GC_BASE_CSS } from '@/src/app/uygulamalar/hmi-pano/pano/game-card-base';
import { paramDeger } from '@/src/app/shared/utils/param-deger.utils';
import { CamParcaEditComponent } from './cam-parca-edit.component';
import { CamParcaKartSadeComponent } from './cam-parca-kart-sade.component';

const DURUM_RENK: Record<string, string> = { 'Bekliyor': '#94a3b8', 'Kütükte': '#3b82f6', "CNC'de": '#a855f7', 'Tamamlandı': '#22c55e', 'Red': '#ff4d4f', 'Hurda': '#434343' };
const DURUM_RGB: Record<string, string> = { 'Bekliyor': '148,163,184', 'Kütükte': '59,130,246', "CNC'de": '168,85,247', 'Tamamlandı': '34,197,94', 'Red': '255,77,79', 'Hurda': '67,67,67' };

@Component({
  selector: 'cam-parca-kart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gc gc--cam-parca" [style.--accent]="renk()" [style.--accent-rgb]="rgb()" (mousemove)="mouseMove($event)" (mouseleave)="mouseLeave()" [ngStyle]="kartStil">
      <div class="gc-canli-bg"></div><div class="gc-parlak-kenar"></div>
      <div class="gc-kose gc-kose--sol-ust"></div><div class="gc-kose gc-kose--sag-ust"></div><div class="gc-kose gc-kose--sol-alt"></div><div class="gc-kose gc-kose--sag-alt"></div>
      <div class="gc-kose-ic gc-kose-ic--sol-ust"></div><div class="gc-kose-ic gc-kose-ic--sag-ust"></div><div class="gc-kose-ic gc-kose-ic--sol-alt"></div><div class="gc-kose-ic gc-kose-ic--sag-alt"></div>
      <div class="gc-rarity-serit"></div><div class="gc-ic-glow"></div><div class="gc-desen"></div><div class="gc-shimmer"></div><div class="gc-holo"></div>
      <div class="gc-kenar-glow" [style.background]="kenarGlow"></div>
      <div class="gc-icerik">
        <div class="gc-hud-baslik">🔷 CAM PARÇA</div>
        <div class="cp-baslik">{{ d('parcaAdi') || 'İsimsiz Parça' }}@if (d('revizyon')) { <span class="cp-rev">{{ d('revizyon') }}</span> }</div>
        @if (gecikiyor()) { <div class="cp-gecikme">⚠️ {{ gecikmeGunu() }} gün gecikmiş</div> }
        @if (d('malzeme')) { <div class="cp-alt">{{ d('malzeme') }}@if (d('tolerans')) { · {{ d('tolerans') }} }</div> }
        <div class="cp-olcu">{{ olcu() }}</div>
        <div class="cp-durum-badge" [style.background]="'rgba('+rgb()+',.25)'" [style.color]="renk()" [style.border-color]="'rgba('+rgb()+',.5)'" [style.box-shadow]="'0 0 10px rgba('+rgb()+',.2)'">{{ sonDurum() }}</div>
        <div class="gc-xp-bar"><div class="gc-xp-dolgu" [style.width.%]="gorunur() ? ilerleme() : 0"></div></div>
      </div>
    </div>
  `,
  styles: [GC_BASE_CSS, `
    .cp-baslik { font-size: 14px; font-weight: 700; color: var(--py-metin, #e2e8f0); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display:flex; align-items:center; gap:6px; }
    .cp-rev { font-size:9px; padding:1px 6px; border-radius:3px; background:rgba(var(--accent-rgb),.2); color:var(--accent); font-weight:600; flex-shrink:0; }
    .cp-alt { font-size: 11px; color: var(--py-metin-ikincil, #94a3b8); }
    .cp-olcu { font-size: 10px; color: var(--py-metin-ucuncul, #64748b); font-family: monospace; }
    .cp-durum-badge { display: inline-block; padding: 3px 12px; border-radius: 4px; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; border: 1.5px solid; margin-top: 4px; backdrop-filter: blur(4px); }
    .cp-gecikme { display:flex; align-items:center; gap:4px; padding:3px 8px; border-radius:3px; background:rgba(255,77,79,.12); border-left:2px solid #ff4d4f; font-size:9px; font-weight:700; color:#ff4d4f; }
  `],
})
export class CamParcaKartComponent extends GameCardBase {
  parametreler = input<any>();
  _hesap = input<any>();
  constructor() { super(); }
  d(ismi: string) { return paramDeger(this.parametreler()?.ek, ismi); }
  olcu = computed(() => { const v = [this.d('en'), this.d('boy'), this.d('yukseklik')].filter(x => x); return v.length ? v.join(' × ') + ' mm' : ''; });
  sonDurum = computed(() => this._hesap()?.sonDurum || this.d('durum') || 'Bekliyor');
  renk = computed(() => DURUM_RENK[this.sonDurum()] || '#94a3b8');
  rgb = computed(() => DURUM_RGB[this.sonDurum()] || '148,163,184');
  ilerleme = computed(() => this.sonDurum() === 'Tamamlandı' ? 100 : this.sonDurum() === "CNC'de" ? 70 : this.sonDurum() === 'Kütükte' ? 40 : this.sonDurum() === 'Red' ? 0 : 10);
  gecikiyor = computed(() => {
    const h = this.d('hedefTarih');
    return h && new Date(h) < new Date() && this.sonDurum() !== 'Tamamlandı';
  });
  gecikmeGunu = computed(() => {
    const h = this.d('hedefTarih');
    if (!h || !this.gecikiyor()) return 0;
    return Math.ceil((Date.now() - new Date(h).getTime()) / 86400000);
  });
}

@DinamikBilesen({
  id: 'cam-parca',
  baslik: 'Cam Parça',
  aciklama: 'Cam parça takibi',
  ikon: 'appstore',
  kategori: 'cam-isleme',
})
class CamParca {
  paylas_kutusu = CamParcaEditComponent;
  paylasilmis_kart = CamParcaKartComponent;
  paylasilmis_kart_sade = CamParcaKartSadeComponent;

  hesaplamalar = {
    prepipe: [
      { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
        { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
        { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
        { $match: { 'ic.ek._secilenSetId': 'cam.parca.durum' } },
        { $sort: { _id: -1 } },
        { $limit: 1 }
      ], as: '_alt' } },
      { $addFields: {
        '_hesap.sonDurum': { $ifNull: [{ $arrayElemAt: ['$_alt.ic.ek.durum', 0] }, null] },
      }},
      { $project: { _alt: 0 } }
    ],
  };
}
